import * as DM from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, Filter } from 'lucide-react';

/* ============================================================================
 * Single "Filter" trigger for the Insights deliverables list, replacing the
 * row of 8 native <select> elements + a toggle button with one Linear-style
 * menu: each filter is a submenu (hover/click to expand), radio-selected,
 * with a badge on the trigger showing how many filters are active.
 * ========================================================================== */

export const DEFAULT_FILTERS = {
  serviceLine: 'ALL',
  activity: 'ALL',
  status: 'ALL',
  owner: 'ALL',
  readiness: 'ALL',
  risk: 'ALL',
  priority: 'ALL',
  dueWithin: 'ALL',
  attentionOnly: false,
};

const STATUS_OPTIONS = ['To Do', 'In Progress', 'Done'].map((v) => ({
  value: v,
  label: v,
}));
const READINESS_OPTIONS = [
  { value: 'READY', label: 'Ready' },
  { value: 'PARTIALLY_READY', label: 'Partially ready' },
  { value: 'NOT_READY', label: 'Not ready' },
  { value: 'AMBIGUOUS', label: 'Ambiguous' },
];
const RISK_OPTIONS = [
  { value: 'ON_TRACK', label: 'On track' },
  { value: 'AT_RISK', label: 'At risk' },
  { value: 'BLOCKED', label: 'Blocked' },
  { value: 'OVERDUE', label: 'Overdue' },
];
const PRIORITY_OPTIONS = [
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
];
const DUE_OPTIONS = [
  { value: '7', label: 'Due within 7 days' },
  { value: '14', label: 'Due within 14 days' },
  { value: '30', label: 'Due within 30 days' },
];

// Was a fully-opaque bg-zinc-900 with no blur — the one surface in this
// toolbar that didn't share the translucent-glass treatment everything
// else uses. Slightly higher opacity than the raised-surface token (70%)
// since this floats over arbitrary scrolled content and needs to stay
// legible, but same zinc-900/zinc-800 family + blur so it reads as part
// of the same system.
const contentCls =
  'z-50 min-w-[190px] overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/95 p-1.5 text-zinc-200 shadow-2xl backdrop-blur-xl';
const itemCls =
  'flex w-full cursor-pointer select-none items-center justify-between gap-3 rounded-md px-2.5 py-2 text-left text-xs text-zinc-300 outline-none transition-colors hover:bg-zinc-800 focus:bg-zinc-800 data-[state=checked]:text-white';
const subTriggerCls =
  'flex w-full cursor-pointer select-none items-center justify-between gap-3 rounded-md px-2.5 py-2 text-left text-xs text-zinc-300 outline-none transition-colors hover:bg-zinc-800 focus:bg-zinc-800 data-[state=open]:bg-zinc-800 data-[state=open]:text-white';

/** One expandable filter row: label on the left, current value + chevron on
 * the right, opening a radio-select submenu of options. */
const FilterSub = ({ label, allLabel, value, options, onChange }) => {
  const current = options.find((o) => o.value === value)?.label;
  return (
    <DM.Sub>
      <DM.SubTrigger className={subTriggerCls}>
        <span>{label}</span>
        <span className="flex min-w-0 items-center gap-1 text-zinc-500">
          {value !== 'ALL' && (
            <span className="max-w-[100px] truncate text-indigo-300">
              {current}
            </span>
          )}
          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        </span>
      </DM.SubTrigger>
      <DM.Portal>
        <DM.SubContent className={contentCls} sideOffset={6} alignOffset={-6}>
          <DM.RadioGroup value={value} onValueChange={onChange}>
            <DM.RadioItem value="ALL" className={itemCls}>
              <span>{allLabel}</span>
              <DM.ItemIndicator>
                <Check className="h-3.5 w-3.5" />
              </DM.ItemIndicator>
            </DM.RadioItem>
            {options.map((o) => (
              <DM.RadioItem key={o.value} value={o.value} className={itemCls}>
                <span>{o.label}</span>
                <DM.ItemIndicator>
                  <Check className="h-3.5 w-3.5" />
                </DM.ItemIndicator>
              </DM.RadioItem>
            ))}
          </DM.RadioGroup>
        </DM.SubContent>
      </DM.Portal>
    </DM.Sub>
  );
};

const FilterMenu = ({
  filters,
  setFilters,
  serviceLines,
  activities,
  owners,
  attentionCount,
}) => {
  const set = (key) => (value) => setFilters({ ...filters, [key]: value });

  const activeCount = Object.entries(filters).filter(([key, v]) =>
    key === 'attentionOnly' ? v === true : v !== 'ALL',
  ).length;

  return (
    <DM.Root>
      <DM.Trigger asChild>
        <button
          type="button"
          className={`flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium transition-all ${
            activeCount > 0
              ? 'border-indigo-500/30 bg-primary-gradient text-white shadow-md shadow-indigo-950/40'
              : 'border-zinc-800/60 bg-zinc-900/60 text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200'
          }`}
        >
          <Filter className="h-3.5 w-3.5" />
          <span>Filter</span>
          {activeCount > 0 && (
            <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-white/20 px-1 text-[10px] font-semibold">
              {activeCount}
            </span>
          )}
        </button>
      </DM.Trigger>
      <DM.Portal>
        <DM.Content className={contentCls} sideOffset={8} align="end">
          {serviceLines.length > 1 && (
            <FilterSub
              label="Service line"
              allLabel="All service lines"
              value={filters.serviceLine}
              options={serviceLines.map((s) => ({ value: s, label: s }))}
              onChange={set('serviceLine')}
            />
          )}
          <FilterSub
            label="Activity"
            allLabel="All activities"
            value={filters.activity}
            options={activities.map((a) => ({ value: a, label: a }))}
            onChange={set('activity')}
          />
          <FilterSub
            label="Status"
            allLabel="Any status"
            value={filters.status}
            options={STATUS_OPTIONS}
            onChange={set('status')}
          />
          <FilterSub
            label="Owner"
            allLabel="Any owner"
            value={filters.owner}
            options={owners.map((o) => ({ value: o, label: o }))}
            onChange={set('owner')}
          />
          <FilterSub
            label="Readiness"
            allLabel="Any readiness"
            value={filters.readiness}
            options={READINESS_OPTIONS}
            onChange={set('readiness')}
          />
          <FilterSub
            label="Risk"
            allLabel="Any risk"
            value={filters.risk}
            options={RISK_OPTIONS}
            onChange={set('risk')}
          />
          <FilterSub
            label="Priority"
            allLabel="Any priority"
            value={filters.priority}
            options={PRIORITY_OPTIONS}
            onChange={set('priority')}
          />
          <FilterSub
            label="Due date"
            allLabel="Any due date"
            value={filters.dueWithin}
            options={DUE_OPTIONS}
            onChange={set('dueWithin')}
          />

          <DM.Separator className="my-1.5 h-px bg-zinc-800" />

          <DM.CheckboxItem
            checked={filters.attentionOnly}
            onCheckedChange={(v) =>
              setFilters({ ...filters, attentionOnly: v })
            }
            className={itemCls}
          >
            <span>
              Needs attention {attentionCount > 0 && `(${attentionCount})`}
            </span>
            <DM.ItemIndicator>
              <Check className="h-3.5 w-3.5" />
            </DM.ItemIndicator>
          </DM.CheckboxItem>

          {activeCount > 0 && (
            <>
              <DM.Separator className="my-1.5 h-px bg-zinc-800" />
              <DM.Item
                onSelect={(e) => {
                  e.preventDefault();
                  setFilters(DEFAULT_FILTERS);
                }}
                className="flex w-full cursor-pointer select-none items-center justify-center rounded-md px-2.5 py-1.5 text-xs font-medium text-zinc-400 outline-none transition-colors hover:bg-zinc-800 hover:text-zinc-200"
              >
                Clear all filters
              </DM.Item>
            </>
          )}
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
};

export default FilterMenu;
